import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { WarningAmber } from '@mui/icons-material';
import { getStatusColor, getPriorityColor } from './utils/maintenanceUtils';
import WorkOrderIssuedPartsPanel from './components/WorkOrderIssuedPartsPanel';
import { getMaintenanceWorkOrderTypeLabel as getTypeLabel } from '../../constants/maintenanceWorkOrderTypes';

const formatUserName = (user) => {
  if (!user) return '—';
  if (typeof user === 'string') return user;
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || '—';
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const DetailField = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{children ?? '—'}</Typography>
  </Grid>
);

const SummaryTable = ({ title, headers, rows, emptyMessage }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
      {title} {rows.length > 0 && `(${rows.length})`}
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
    ) : (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

const MroQmPendingWorkOrderDetails = ({
  workOrder,
  loading,
  error,
  mroQmForm,
  canAct,
  onMroQmFieldChange,
  onSubmitMroQmAction,
  onDownloadCrs,
  onDownloadCameForm,
  onOpenInMaintenance
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!workOrder) {
    return <Alert severity="info">Work order details are not available.</Alert>;
  }

  const ampCheckRows = (workOrder.ampChecks || []).map((check) => [
    check.sequenceLabel || check.sequenceNumber,
    check.description || '—',
    check.taskReferenceId || '—',
    check.status || 'PENDING'
  ]);

  const spareRows = (workOrder.requiredSpares || []).map((item) => [
    item.partNumber || '—',
    item.quantity ?? '—',
    item.itemName || '—',
    item.taskReference || '—'
  ]);

  const consumableRows = (workOrder.requiredConsumables || []).map((item) => [
    item.partNumberOrSpecification || '—',
    item.quantity ?? '—',
    item.itemName || '—',
    item.expiry ? dayjs(item.expiry).format('YYYY-MM-DD') : '—'
  ]);

  const toolRows = (workOrder.requiredToolsEquipment || []).map((item) => [
    item.partNumber || '—',
    item.itemName || '—',
    item.due ? dayjs(item.due).format('YYYY-MM-DD') : '—',
    item.taskReferenceId || '—'
  ]);

  const verificationRows = (workOrder.verificationChecks || []).map((item) => [
    item.task || '—',
    item.status || 'PENDING'
  ]);

  const procurement = workOrder.partsProcurementRequirements;
  const procurementRows = (procurement?.partNumbers || []).map((pn, index) => [
    pn,
    procurement.quantities?.[index] ?? '—',
    procurement.descriptions?.[index] || '—'
  ]);

  const assemblyLabels = (workOrder.assemblies || [])
    .map((assembly) => {
      if (!assembly) return null;
      if (typeof assembly === 'string') return assembly;
      const parts = [assembly.typeName, assembly.serialNumber, assembly.position].filter(Boolean);
      return parts.join(' · ') || assembly._id;
    })
    .filter(Boolean);


  return (
    <Box sx={{ py: 1 }}>
      <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
        Review the complete work order record below, then approve, reject, or return to MRO engineer.
      </Alert>

      {workOrder.mroQmCompletionRejectedAt && !workOrder.mroQmCompletionAcknowledged && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Previously rejected by MRO QM: {workOrder.mroQmCompletionRejectionRemarks || 'No remarks recorded.'}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          <Typography variant="h6">Work Order Summary</Typography>
          {onOpenInMaintenance && (
            <Button size="small" variant="outlined" onClick={() => onOpenInMaintenance(workOrder._id)}>
              Open in Maintenance
            </Button>
          )}
        </Stack>
        <Grid container spacing={2}>
          <DetailField label="Work Order #">{workOrder.workOrderNumber || workOrder.woNumber || '—'}</DetailField>
          <DetailField label="Title">{workOrder.title || '—'}</DetailField>
          <DetailField label="Aircraft">{workOrder.aircraftId?.registration || 'Unknown'}</DetailField>
          <DetailField label="Aircraft Model">
            {[workOrder.aircraftId?.manufacturer, workOrder.aircraftId?.model].filter(Boolean).join(' ') || '—'}
          </DetailField>
          <DetailField label="Type">{getTypeLabel(workOrder.type)}</DetailField>
          <DetailField label="Status">
            <Chip label={workOrder.status} color={getStatusColor(workOrder.status)} size="small" />
          </DetailField>
          <DetailField label="Priority">
            <Chip label={workOrder.priority} color={getPriorityColor(workOrder.priority)} size="small" />
          </DetailField>
          <DetailField label="Scheduled Date">{formatDate(workOrder.scheduledDate || workOrder.dueDate)}</DetailField>
          <DetailField label="WO Date">{formatDate(workOrder.woDate)}</DetailField>
          <DetailField label="Maintenance Type">{workOrder.maintenanceType || '—'}</DetailField>
          <DetailField label="Assemblies">
            {assemblyLabels.length ? assemblyLabels.join('; ') : '—'}
          </DetailField>
          <DetailField label="MRO Accepted">{workOrder.mroAccepted ? 'Yes' : 'No'}</DetailField>
          <DetailField label="Current Shift Engineer">
            {formatUserName(workOrder.mroCurrentEngineerId || workOrder.mroAssignedEngineerId)}
          </DetailField>
          <DetailField label="Engineer Team">
            {Array.isArray(workOrder.mroAssignedEngineerIds) && workOrder.mroAssignedEngineerIds.length
              ? workOrder.mroAssignedEngineerIds.map((e) => formatUserName(e)).filter(Boolean).join(', ') || '—'
              : formatUserName(workOrder.mroAssignedEngineerId)}
          </DetailField>
          <DetailField label="Planning Approved">{workOrder.planningApproved ? 'Yes' : 'No'}</DetailField>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">Description</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {workOrder.description || 'No description'}
            </Typography>
          </Grid>
          {workOrder.creationSourceRemarks && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">Creation Source / Reason</Typography>
              <Typography variant="body2">{workOrder.creationSourceRemarks}</Typography>
            </Grid>
          )}
          {workOrder.planningNotes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">Planning Notes</Typography>
              <Typography variant="body2">{workOrder.planningNotes}</Typography>
            </Grid>
          )}
          {workOrder.mroNotes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">MRO Notes</Typography>
              <Typography variant="body2">{workOrder.mroNotes}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>MRO Engineer Completion</Typography>
        <Grid container spacing={2}>
          <DetailField label="Completed">{workOrder.mroEngineerCompleted ? 'Yes' : 'No'}</DetailField>
          <DetailField label="Completed By">{formatUserName(workOrder.mroEngineerCompletedBy)}</DetailField>
          <DetailField label="Completed At">{formatDate(workOrder.mroEngineerCompletedAt)}</DetailField>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">Engineer Notes</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {workOrder.mroEngineerNotes || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {onDownloadCameForm && (
                <Button size="small" variant="contained" onClick={() => onDownloadCameForm(workOrder)}>
                  Download work package (operator letterpad)
                </Button>
              )}
              {workOrder.crsDocument?.originalName && onDownloadCrs && (
                <Button size="small" variant="outlined" onClick={() => onDownloadCrs(workOrder)}>
                  Download CRS ({workOrder.crsDocument.originalName})
                </Button>
              )}
              {workOrder.workOrderSecondPage?.savedAt && (
                <Chip size="small" color="success" variant="outlined" label="Action Taken Form saved" />
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Issued Parts</Typography>
        <WorkOrderIssuedPartsPanel workOrderId={workOrder._id} />
      </Box>

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Materials & Checks</Typography>
      <SummaryTable title="Checklist" headers={['S/N', 'Description', 'Task Ref', 'Status']} rows={ampCheckRows} emptyMessage="No checklist items on this work order." />
      <SummaryTable title="Required Spares" headers={['Part Number', 'Qty', 'Description', 'Task Ref']} rows={spareRows} emptyMessage="No required spares listed." />
      <SummaryTable title="Required Consumables" headers={['Part Number / Spec', 'Qty', 'Description', 'Expiry']} rows={consumableRows} emptyMessage="No required consumables listed." />
      <SummaryTable title="Tools & Equipment" headers={['Part Number', 'Description', 'Due', 'Task Ref']} rows={toolRows} emptyMessage="No required tools listed." />
      <SummaryTable title="Verification Checks" headers={['Task', 'Status']} rows={verificationRows} emptyMessage="No verification checks listed." />
      <SummaryTable title="Parts Procurement Requirements" headers={['Part Number', 'Qty', 'Description']} rows={procurementRows} emptyMessage="No procurement requirements submitted yet." />

      {canAct && (
        <>
          <Divider sx={{ my: 2 }} />
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>MRO QM Decision</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Approve to send to CAMO QM. Reject records your objection while engineer completion remains on file.
              Back to engineer returns the work order to the previous step for rework.
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Approval notes (optional)"
              value={mroQmForm.notes}
              onChange={(e) => onMroQmFieldChange(workOrder._id, 'notes', e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks (required for Reject / Back to Engineer)"
              value={mroQmForm.remarks}
              onChange={(e) => onMroQmFieldChange(workOrder._id, 'remarks', e.target.value)}
              sx={{ mb: 2 }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                color="success"
                disabled={mroQmForm.loading}
                onClick={() => onSubmitMroQmAction(workOrder, 'ACKNOWLEDGE')}
              >
                {mroQmForm.loading ? 'Submitting...' : 'Approve'}
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={mroQmForm.loading || !mroQmForm.remarks.trim()}
                onClick={() => onSubmitMroQmAction(workOrder, 'REJECT')}
              >
                Reject
              </Button>
              <Button
                variant="outlined"
                color="warning"
                disabled={mroQmForm.loading || !mroQmForm.remarks.trim()}
                onClick={() => onSubmitMroQmAction(workOrder, 'BACK_TO_ENGINEER')}
              >
                Back to Engineer
              </Button>
            </Stack>

            {mroQmForm.error && <Alert severity="error" sx={{ mt: 2 }}>{mroQmForm.error}</Alert>}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MroQmPendingWorkOrderDetails;
